import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Box, Text, Badge, Flex,
  Input, Textarea, Select, Switch, FormControl, FormLabel, Divider,
  IconButton, Tooltip, useToast, Tabs, TabList, TabPanels, Tab, TabPanel,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  Table, Thead, Tbody, Tr, Th, Td, Checkbox, CheckboxGroup, Stack,
  Alert, AlertIcon, Spinner,
} from '@chakra-ui/react';
import {
  FiPlus, FiTrash2, FiLink, FiLock, FiUnlock, FiArchive, FiEye,
  FiMessageSquare, FiStar, FiUser, FiCopy, FiExternalLink, FiRefreshCw,
  FiCheck, FiEdit2, FiFilm, FiFile,
} from 'react-icons/fi';
import api from '../api';
import FileUpload from './FileUpload';

const ORIGIN = window.location.origin;

const fmtDt = (d) => d ? new Date(d).toLocaleString('en-GB', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—';
const fmtDur = (s) => { if (!s) return '—'; const m = Math.floor(s/60); const sec = s%60; return m > 0 ? `${m}m ${sec}s` : `${sec}s`; };

function ContactRow({ c, itemId, onRemove }) {
  const link = `${ORIGIN}/feedback/${c.token}`;
  const toast = useToast();
  const approved = !!(c.is_approved || c.approval_count > 0);
  return (
    <Tr>
      <Td px={2} fontSize="12px" fontWeight="600">{c.first_name ? [c.first_name, c.last_name].filter(Boolean).join(' ') : c.label || '—'}</Td>
      <Td px={2} fontSize="11px" color="gray.500">{c.email || '—'}</Td>
      <Td px={2}>
        <HStack spacing={1}>
          <Tooltip label="Copy link" fontSize="xs">
            <IconButton icon={<FiCopy size={12} />} size="xs" variant="ghost" colorScheme="blue"
              onClick={() => { navigator.clipboard.writeText(link); toast({ title:'Link copied', status:'success', duration:1500 }); }} />
          </Tooltip>
          <Tooltip label="Open link" fontSize="xs">
            <IconButton icon={<FiExternalLink size={12} />} size="xs" variant="ghost" colorScheme="blue"
              onClick={() => window.open(link, '_blank')} />
          </Tooltip>
        </HStack>
      </Td>
      <Td px={2} fontSize="11px">{c.view_count > 0 ? `${c.view_count}× (${fmtDur(c.total_view_seconds)})` : <Badge fontSize="8px" colorScheme="gray">Not viewed</Badge>}</Td>
      <Td px={2}>
        {approved
          ? <Badge colorScheme="green" fontSize="8px">✅ Approved{c.approved_at ? ` ${new Date(c.approved_at).toLocaleDateString('en-GB',{day:'numeric',month:'short'})}` : ''}</Badge>
          : <Badge colorScheme="gray" variant="outline" fontSize="8px">Not yet approved</Badge>}
      </Td>
      <Td px={2} fontSize="11px">{c.response_count > 0 ? <Badge colorScheme="purple" fontSize="8px">{c.response_count} response{c.response_count!==1?'s':''}</Badge> : '—'}</Td>
      <Td px={2} fontSize="11px" color="gray.400">{c.last_ip || '—'}</Td>
      <Td px={1}>
        <IconButton icon={<FiTrash2 size={12} />} size="xs" variant="ghost" colorScheme="red"
          onClick={() => onRemove(c.id)} />
      </Td>
    </Tr>
  );
}

function ResponseRow({ r }) {
  const stars = r.score ? '★'.repeat(r.score) + '☆'.repeat(5 - r.score) : null;
  const fmtTs = (s) => { if (!s) return null; const m = Math.floor(s/60); return `${m}:${String(s%60).padStart(2,'0')}`; };
  return (
    <Box p={3} border="1px" borderColor="gray.100" borderRadius="md" bg="white">
      <Flex justify="space-between" align="center" mb={1}>
        <HStack spacing={2}>
          <Text fontSize="12px" fontWeight="700" color="gray.700">{r.respondent_name || 'Anonymous'}</Text>
          {r.email && <Text fontSize="10px" color="gray.400">{r.email}</Text>}
        </HStack>
        <HStack spacing={2}>
          {stars && <Text fontSize="14px" color="yellow.400">{stars}</Text>}
          <Text fontSize="10px" color="gray.400">{fmtDt(r.created_at)}</Text>
        </HStack>
      </Flex>
      {r.video_timestamp && <Badge colorScheme="purple" fontSize="8px" mb={1}>⏱ {fmtTs(r.video_timestamp)}</Badge>}
      {r.feedback_text && <Text fontSize="13px" color="gray.700" whiteSpace="pre-wrap">{r.feedback_text}</Text>}
    </Box>
  );
}

export default function FeedbackModal({ taskId, taskTitle, projectId, subTasks = [], isOpen, onClose, onRefresh }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [responses, setResponses] = useState([]);
  const [views, setViews] = useState([]);
  const [creating, setCreating] = useState(false);
  const [versionSourceId, setVersionSourceId] = useState(null); // source item when creating a New Version (copies its contacts)
  const [editingItem, setEditingItem] = useState(null); // item being edited from list (not detail view)
  const [editing, setEditing] = useState(false); // editing within the detail view tabs
  const [addingContact, setAddingContact] = useState(false);
  const [assets, setAssets] = useState([]);
  const [attachedFileName, setAttachedFileName] = useState('');
  const [addingAsset, setAddingAsset] = useState(false);
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [assetForm, setAssetForm] = useState({ title:'', description:'', asset_type:'link', url:'' });
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [projectContacts, setProjectContacts] = useState([]); // initial list for search
  const [fetchedSubTasks, setFetchedSubTasks] = useState([]);
  const [subTasksLoaded, setSubTasksLoaded] = useState(false);
  const [form, setForm] = useState({
    title: taskTitle || '', description: '', status_update: '', version_number: '1.0',
    content_type: 'youtube', content_url: '', file_id: null,
    allow_resharing: false, password: '', feedback_types: ['text'],
    contacts_can_see_all_feedback: false, sub_task_id: '',
  });
  const toast = useToast();
  const allSubTasks = subTasks.length > 0 ? subTasks : fetchedSubTasks;

  const fetchItems = useCallback(async () => {
    if (!taskId) return;
    setLoading(true);
    try {
      const [fbRes, stRes] = await Promise.all([
        api.get(`/feedback?task_id=${taskId}`),
        api.get(`/sub-tasks?task_id=${taskId}`).catch(() => ({ data: [] })),
      ]);
      setItems(fbRes.data || []);
      if (stRes.data?.length) setFetchedSubTasks(stRes.data);
      setSubTasksLoaded(true);
    } catch(e) {}
    setLoading(false);
  }, [taskId]);

  const fetchContacts = useCallback(async (itemId) => {
    try { const { data } = await api.get(`/feedback/${itemId}/contacts`); setContacts(data || []); } catch(e) {}
  }, []);

  const fetchResponses = useCallback(async (itemId) => {
    try { const { data } = await api.get(`/feedback/${itemId}/responses`); setResponses(data || []); } catch(e) {}
  }, []);

  const fetchViews = useCallback(async (itemId) => {
    try { const { data } = await api.get(`/feedback/${itemId}/views`); setViews(data || []); } catch(e) {}
  }, []);

  const fetchAssets = useCallback(async (itemId) => {
    try { const { data } = await api.get(`/feedback/${itemId}/assets`); setAssets(data || []); } catch(e) {}
  }, []);

  // Pre-load project/task contacts for the initial "Add Contact" dropdown
  const fetchProjectContacts = useCallback(async () => {
    try {
      const results = await Promise.all([
        projectId ? api.get(`/projects/${projectId}/contacts`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        taskId    ? api.get(`/tasks/${taskId}/contacts`).catch(() => ({ data: [] }))    : Promise.resolve({ data: [] }),
      ]);
      const merged = [];
      const seen = new Set();
      [...(results[0].data || []), ...(results[1].data || [])].forEach(c => {
        const id = c.id || c.contact_id;
        if (id && !seen.has(id)) { seen.add(id); merged.push(c); }
      });
      setProjectContacts(merged);
    } catch(e) {}
  }, [projectId, taskId]);

  useEffect(() => { if (isOpen) { fetchItems(); fetchProjectContacts(); } }, [isOpen, fetchItems, fetchProjectContacts]);

  useEffect(() => {
    if (selected) {
      fetchContacts(selected.uuid || selected.id);
      fetchResponses(selected.uuid || selected.id);
      fetchAssets(selected.uuid || selected.id);
      fetchViews(selected.uuid || selected.id);
    }
  }, [selected, fetchContacts, fetchResponses, fetchAssets, fetchViews]);

  const searchContacts = async (q) => {
    try {
      if (!q.trim()) {
        // Show project/task contacts first, fallback to all contacts
        if (projectContacts.length > 0) {
          setContactResults(projectContacts);
        } else {
          const { data } = await api.get(`/contacts/search?q=&limit=50`);
          setContactResults(Array.isArray(data) ? data : []);
        }
      } else {
        const { data } = await api.get(`/contacts/search?q=${encodeURIComponent(q)}&limit=50`);
        setContactResults(Array.isArray(data) ? data : []);
      }
    } catch(e) {}
  };

  const handleCreate = async () => {
    try {
      const payload = { ...form, task_id: taskId, project_id: projectId };
      if (!payload.feedback_types.length) payload.feedback_types = ['text'];
      if (versionSourceId) payload.copy_contacts_from = versionSourceId; // New Version → inherit original's contacts
      const { data } = await api.post('/feedback', payload);
      setItems(prev => [data, ...prev]);
      setCreating(false);
      setVersionSourceId(null);
      setSelected(data);
      toast({ title: 'Feedback item created', status: 'success', duration: 2000 });
      if (onRefresh) onRefresh();
    } catch(e) { toast({ title: 'Failed to create', status: 'error', duration: 2000 }); }
  };

  const handleUpdate = async () => {
    try {
      const { data } = await api.put(`/feedback/${selected.uuid || selected.id}`, form);
      setItems(prev => prev.map(i => i.id === data.id ? data : i));
      setSelected(data); setEditing(false);
      toast({ title: 'Updated', status: 'success', duration: 1500 });
    } catch(e) { toast({ title: 'Update failed', status: 'error', duration: 2000 }); }
  };

  const handleArchive = async (item, archive) => {
    await api.patch(`/feedback/${item.uuid || item.id}/archive`, { archive });
    await fetchItems();
    if (selected?.id === item.id) setSelected(null);
    toast({ title: archive ? 'Archived' : 'Unarchived', status: 'info', duration: 1500 });
  };

  const handleDelete = async (item) => {
    await api.delete(`/feedback/${item.uuid || item.id}`);
    setItems(prev => prev.filter(i => i.id !== item.id));
    if (selected?.id === item.id) setSelected(null);
    if (onRefresh) onRefresh();
  };

  const addContact = async (contact) => {
    try {
      const { data } = await api.post(`/feedback/${selected.uuid || selected.id}/contacts`, {
        contact_id: contact.uuid || contact.id,
        label: [contact.first_name, contact.last_name].filter(Boolean).join(' ') || contact.email || '—',
      });
      setContacts(prev => [...prev, data]);
      setContactSearch(''); setContactResults([]); setAddingContact(false);
    } catch(e) { toast({ title: 'Failed to add contact', status: 'error', duration: 2000 }); }
  };

  const removeContact = async (contactId) => {
    await api.delete(`/feedback/${selected.uuid || selected.id}/contacts/${contactId}`);
    setContacts(prev => prev.filter(c => c.id !== contactId));
  };

  const formSet = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleAddAsset = async () => {
    if (!assetForm.title && !assetForm.url) return;
    try {
      const { data } = await api.post(`/feedback/${selected.uuid || selected.id}/assets`, assetForm);
      setAssets(prev => [...prev, data]);
      setAssetForm({ title:'', description:'', asset_type:'link', url:'' });
      setAddingAsset(false);
    } catch(e) { toast({ title:'Failed to add asset', status:'error', duration:2000 }); }
  };

  const handleUpdateAsset = async (assetId) => {
    try {
      await api.put(`/feedback/${selected.uuid || selected.id}/assets/${assetId}`, assetForm);
      setAssets(prev => prev.map(a => a.id === assetId ? { ...a, ...assetForm } : a));
      setEditingAssetId(null);
    } catch(e) { toast({ title:'Failed to update', status:'error', duration:2000 }); }
  };

  const handleDeleteAsset = async (assetId) => {
    await api.delete(`/feedback/${selected.uuid || selected.id}/assets/${assetId}`);
    setAssets(prev => prev.filter(a => a.id !== assetId));
  };

  const contentFormJSX = (
    <VStack spacing={3} align="stretch">
      <HStack spacing={3} flexWrap="wrap">
        <FormControl w="160px">
          <FormLabel fontSize="xs" mb={0.5}>Content Type</FormLabel>
          <Select size="sm" value={form.content_type} onChange={e => formSet('content_type', e.target.value)}>
            <option value="youtube">YouTube Video</option>
            <option value="vimeo">Vimeo Video</option>
            <option value="link">Website / Link</option>
            <option value="pdf">PDF Document</option>
            <option value="document">File / Document</option>
            <option value="image">Image</option>
          </Select>
        </FormControl>
        <FormControl flex={1}>
          <FormLabel fontSize="xs" mb={0.5}>Version</FormLabel>
          <Input size="sm" value={form.version_number} onChange={e => formSet('version_number', e.target.value)} placeholder="1.0" />
        </FormControl>
      </HStack>
      <FormControl>
        <FormLabel fontSize="xs" mb={0.5}>Title</FormLabel>
        <Input size="sm" value={form.title} onChange={e => formSet('title', e.target.value)} placeholder="What are you sharing?" />
      </FormControl>
      {(form.content_type === 'youtube' || form.content_type === 'vimeo' || form.content_type === 'link') && (
        <FormControl>
          <FormLabel fontSize="xs" mb={0.5}>{form.content_type === 'youtube' ? 'YouTube URL' : form.content_type === 'vimeo' ? 'Vimeo URL' : 'URL / Link'}</FormLabel>
          <Input size="sm" value={form.content_url} onChange={e => formSet('content_url', e.target.value)}
            placeholder={form.content_type === 'youtube' ? 'https://youtube.com/watch?v=...' : form.content_type === 'vimeo' ? 'https://vimeo.com/...' : 'https://'} />
        </FormControl>
      )}
      {(form.content_type === 'pdf' || form.content_type === 'document' || form.content_type === 'image') && (
        <FormControl>
          <FormLabel fontSize="xs" mb={0.5}>Upload File</FormLabel>
          <FileUpload entityType="project" entityId={projectId} folder="documents"
            accept={form.content_type === 'image' ? 'image/*' : form.content_type === 'pdf' ? '.pdf' : '*'}
            label="Attach file" multiple={false}
            onUploaded={(f) => { formSet('file_id', f.id); setAttachedFileName(f.original_name || f.file_name || f.stored_name || 'Attached file'); }} />
          {form.file_id ? (
            <Text fontSize="11px" color="green.600" mt={1}>✓ This file will be sent to the reviewer to view/download{attachedFileName ? `: ${attachedFileName}` : ''}.</Text>
          ) : (
            <Text fontSize="11px" color="orange.500" mt={1}>Upload a file above so the reviewer has something to download.</Text>
          )}
        </FormControl>
      )}
      <FormControl>
        <FormLabel fontSize="xs" mb={0.5}>Description</FormLabel>
        <Textarea size="sm" rows={3} value={form.description} onChange={e => formSet('description', e.target.value)} placeholder="Context for the reviewer…" />
      </FormControl>
      <FormControl>
        <FormLabel fontSize="xs" mb={0.5}>Status Update (shown prominently)</FormLabel>
        <Textarea size="sm" rows={2} value={form.status_update} onChange={e => formSet('status_update', e.target.value)} placeholder="Latest update for reviewers…" />
      </FormControl>
      {allSubTasks.length > 0 && (
        <FormControl>
          <FormLabel fontSize="xs" mb={0.5}>Attribute to Sub-task (optional)</FormLabel>
          <Select size="sm" value={form.sub_task_id} onChange={e => formSet('sub_task_id', e.target.value)}>
            <option value="">Task only</option>
            {allSubTasks.map(s => <option key={s.id} value={s.id}>{s.title || s.description || `Sub-task ${s.id}`}</option>)}
          </Select>
        </FormControl>
      )}
    </VStack>
  );

  const settingsFormJSX = (
    <VStack spacing={3} align="stretch">
      <FormControl>
        <FormLabel fontSize="xs" mb={1}>Feedback Types Requested</FormLabel>
        <CheckboxGroup value={form.feedback_types} onChange={v => formSet('feedback_types', v)}>
          <Stack direction="row" spacing={4}>
            <Checkbox value="score" size="sm">Score (1–5 stars)</Checkbox>
            <Checkbox value="text" size="sm">Written feedback</Checkbox>
            <Checkbox value="file" size="sm">File attachment</Checkbox>
          </Stack>
        </CheckboxGroup>
      </FormControl>
      <HStack justify="space-between" align="center" p={3} bg="gray.50" borderRadius="md">
        <Box>
          <Text fontSize="12px" fontWeight="600">Allow resharing</Text>
          <Text fontSize="10px" color="gray.500">Contacts can share the link with others</Text>
        </Box>
        <Switch isChecked={form.allow_resharing} onChange={e => formSet('allow_resharing', e.target.checked)} colorScheme="teal" />
      </HStack>
      <HStack justify="space-between" align="center" p={3} bg="gray.50" borderRadius="md">
        <Box>
          <Text fontSize="12px" fontWeight="600">Contacts can see all feedback</Text>
          <Text fontSize="10px" color="gray.500">Reviewers see everyone's responses, not just their own</Text>
        </Box>
        <Switch isChecked={form.contacts_can_see_all_feedback} onChange={e => formSet('contacts_can_see_all_feedback', e.target.checked)} colorScheme="purple" />
      </HStack>
      <FormControl>
        <FormLabel fontSize="xs" mb={0.5}>Password (optional)</FormLabel>
        <Input size="sm" type="password" value={form.password} onChange={e => formSet('password', e.target.value)} placeholder="Leave blank to keep existing / no password" />
        {(editingItem?.password_hash || editingItem?.password_protected) ? (
          <HStack mt={1} spacing={2}>
            <Badge colorScheme="orange" fontSize="8px">🔒 Password currently set</Badge>
            <Button size="xs" colorScheme="red" variant="ghost"
              onClick={() => formSet('password', '__CLEAR__')}>
              Remove password
            </Button>
          </HStack>
        ) : null}
        {form.password === '__CLEAR__' && (
          <Badge colorScheme="green" fontSize="8px" mt={1}>✓ Password will be removed on save</Badge>
        )}
      </FormControl>
    </VStack>
  );

  if (selected) {
    const link = `${ORIGIN}/feedback/${selected.token || ''}`;
    return (
      <Modal isOpen={isOpen} onClose={() => { setSelected(null); onClose(); }} size="4xl" scrollBehavior="inside">
        <ModalOverlay /><ModalContent maxH="80vh" mb="60px">
          <ModalHeader fontSize="md" pb={2}>
            <Flex justify="space-between" align="center" pr={8}>
              <HStack spacing={2}>
                <FiMessageSquare />
                <Text>Client Feedback</Text>
                <Badge colorScheme="purple" fontSize="9px">v{selected.version_number}</Badge>
                {selected.is_archived && <Badge colorScheme="gray" fontSize="9px">Archived</Badge>}
              </HStack>
              <HStack spacing={2}>

                <Button size="xs" leftIcon={<FiArchive size={11} />} variant="outline" colorScheme={selected.is_archived?'teal':'orange'}
                  onClick={() => handleArchive(selected, !selected.is_archived)}>
                  {selected.is_archived ? 'Unarchive' : 'Archive'}
                </Button>
                <Button size="xs" leftIcon={<FiPlus size={11} />} colorScheme="purple"
                  onClick={() => { setVersionSourceId(selected.uuid || selected.id); setSelected(null); setCreating(true); setForm(p => ({...p, version_number: String(parseFloat(selected.version_number||'1')+0.1).slice(0,3) })); }}>
                  New Version
                </Button>
              </HStack>
            </Flex>
          </ModalHeader>
          <ModalCloseButton onClick={() => setSelected(null)} />
          <ModalBody>
            <Tabs colorScheme="purple" size="sm">
              <TabList>
                <Tab>Overview</Tab>
                <Tab>Assets ({assets.length})</Tab>
                <Tab>Contacts & Links ({contacts.length})</Tab>
                <Tab>Views ({views.length})</Tab>
                <Tab>Responses ({responses.length})</Tab>
              </TabList>
              <TabPanels>
                {/* Overview */}
                <TabPanel px={0}>
                  <VStack spacing={3} align="stretch">
                    <Box p={3} bg="purple.50" borderRadius="md">
                      <Text fontSize="16px" fontWeight="800" color="purple.800">{selected.title}</Text>
                      {selected.description && <Text fontSize="12px" color="gray.600" mt={1}>{selected.description}</Text>}
                      {selected.status_update && (
                        <Box mt={2} p={2} bg="purple.100" borderRadius="md">
                          <Text fontSize="10px" fontWeight="700" color="purple.600" textTransform="uppercase" mb={0.5}>Status Update</Text>
                          <Text fontSize="12px" color="purple.800">{selected.status_update}</Text>
                        </Box>
                      )}
                    </Box>

                    {/* Approval summary */}
                    {contacts.length > 0 && (() => {
                      const approvedCount = contacts.filter(c => c.is_approved || c.approval_count > 0).length;
                      const total = contacts.length;
                      return (
                        <Box p={3} borderRadius="md" border="1px solid"
                          borderColor={approvedCount === total ? 'green.300' : approvedCount > 0 ? 'orange.200' : 'gray.200'}
                          bg={approvedCount === total ? 'green.50' : approvedCount > 0 ? 'orange.50' : 'gray.50'}>
                          <HStack spacing={2} flexWrap="wrap">
                            <Text fontSize="12px" fontWeight="700"
                              color={approvedCount === total ? 'green.700' : approvedCount > 0 ? 'orange.700' : 'gray.500'}>
                              {approvedCount === total ? '✅ Fully Approved' : approvedCount > 0 ? `⏳ ${approvedCount} of ${total} approved` : '⏳ Awaiting approval'}
                            </Text>
                            {contacts.map(c => {
                              const approved = c.is_approved || c.approval_count > 0;
                              const name = c.label || [c.first_name, c.last_name].filter(Boolean).join(' ') || '—';
                              return (
                                <Badge key={c.id} colorScheme={approved ? 'green' : 'gray'} fontSize="8px">
                                  {approved ? `✓ ${name}` : name}
                                </Badge>
                              );
                            })}
                          </HStack>
                        </Box>
                      );
                    })()}

                    <HStack spacing={2} flexWrap="wrap">
                      <Badge colorScheme="blue" fontSize="9px">{selected.content_type}</Badge>
                      {selected.sub_task_id && (
                        <Badge colorScheme="purple" fontSize="9px">
                          🔹 {allSubTasks.find(s=>s.id===selected.sub_task_id)?.title || allSubTasks.find(s=>s.id===selected.sub_task_id)?.description || `Sub-task`}
                        </Badge>
                      )}
                      {!selected.sub_task_id && <Badge colorScheme="gray" fontSize="9px">Task level</Badge>}
                      {selected.allow_resharing && <Badge colorScheme="teal" fontSize="9px">Resharing allowed</Badge>}
                      {selected.contacts_can_see_all_feedback && <Badge colorScheme="purple" fontSize="9px">Open feedback</Badge>}
                      {selected.password_hash && <Badge colorScheme="orange" fontSize="9px">🔒 Password protected</Badge>}
                    </HStack>
                    {selected.content_url && (
                      <HStack p={2} bg="gray.50" borderRadius="md">
                        <FiLink size={12} />
                        <Text fontSize="11px" noOfLines={1} flex={1}>{selected.content_url}</Text>
                        <IconButton icon={<FiExternalLink size={12} />} size="xs" variant="ghost"
                          onClick={() => window.open(selected.content_url, '_blank')} />
                      </HStack>
                    )}
                  </VStack>
                </TabPanel>

                {/* Additional Assets */}
                <TabPanel px={0}>
                  <VStack spacing={2} align="stretch">
                    <Flex justify="space-between" align="center">
                      <Text fontSize="12px" color="gray.500">Additional files, links or videos alongside the main asset</Text>
                      <Button size="xs" leftIcon={<FiPlus size={11} />} bg="#EE7203" color="white" _hover={{ bg:'#c05600' }}
                        onClick={() => { setAssetForm({ title:'', description:'', asset_type:'link', url:'' }); setAddingAsset(true); setEditingAssetId(null); }}>
                        Add Asset
                      </Button>
                    </Flex>

                    {addingAsset && (
                      <Box p={3} bg="orange.50" borderRadius="md" border="1px" borderColor="orange.200">
                        <Text fontSize="11px" fontWeight="700" color="orange.700" mb={2}>New Asset</Text>
                        <VStack spacing={2} align="stretch">
                          <HStack spacing={2}>
                            <FormControl w="150px">
                              <FormLabel fontSize="xs" mb={0.5}>Type</FormLabel>
                              <Select size="xs" value={assetForm.asset_type} onChange={e => setAssetForm(p => ({...p, asset_type: e.target.value}))}>
                                <option value="link">Link</option>
                                <option value="youtube">YouTube</option>
                                <option value="vimeo">Vimeo</option>
                                <option value="pdf">PDF</option>
                                <option value="document">Document</option>
                                <option value="image">Image</option>
                              </Select>
                            </FormControl>
                            <FormControl flex={1}>
                              <FormLabel fontSize="xs" mb={0.5}>Title</FormLabel>
                              <Input size="xs" value={assetForm.title} onChange={e => setAssetForm(p => ({...p, title: e.target.value}))} placeholder="Asset title" />
                            </FormControl>
                          </HStack>
                          <FormControl>
                            <FormLabel fontSize="xs" mb={0.5}>URL</FormLabel>
                            <Input size="xs" value={assetForm.url} onChange={e => setAssetForm(p => ({...p, url: e.target.value}))} placeholder="https://..." />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="xs" mb={0.5}>Description (optional)</FormLabel>
                            <Textarea size="xs" rows={2} value={assetForm.description} onChange={e => setAssetForm(p => ({...p, description: e.target.value}))} placeholder="Brief description…" />
                          </FormControl>
                          <HStack justify="flex-end">
                            <Button size="xs" variant="ghost" onClick={() => setAddingAsset(false)}>Cancel</Button>
                            <Button size="xs" bg="#EE7203" color="white" _hover={{ bg:'#c05600' }} onClick={handleAddAsset}>Add</Button>
                          </HStack>
                        </VStack>
                      </Box>
                    )}

                    {assets.length === 0 && !addingAsset && (
                      <Text fontSize="12px" color="gray.400" textAlign="center" py={4}>No additional assets yet</Text>
                    )}

                    {assets.map(asset => (
                      <Box key={asset.id} p={3} border="1px" borderColor="gray.100" borderRadius="md" bg="white">
                        {editingAssetId === asset.id ? (
                          <VStack spacing={2} align="stretch">
                            <HStack spacing={2}>
                              <Select size="xs" w="140px" value={assetForm.asset_type} onChange={e => setAssetForm(p => ({...p, asset_type: e.target.value}))}>
                                <option value="link">Link</option>
                                <option value="youtube">YouTube</option>
                                <option value="vimeo">Vimeo</option>
                                <option value="pdf">PDF</option>
                                <option value="document">Document</option>
                                <option value="image">Image</option>
                              </Select>
                              <Input size="xs" flex={1} value={assetForm.title} onChange={e => setAssetForm(p => ({...p, title: e.target.value}))} placeholder="Title" />
                            </HStack>
                            <Input size="xs" value={assetForm.url} onChange={e => setAssetForm(p => ({...p, url: e.target.value}))} placeholder="URL" />
                            <Textarea size="xs" rows={2} value={assetForm.description} onChange={e => setAssetForm(p => ({...p, description: e.target.value}))} placeholder="Description" />
                            <HStack justify="flex-end">
                              <Button size="xs" variant="ghost" onClick={() => setEditingAssetId(null)}>Cancel</Button>
                              <Button size="xs" bg="#EE7203" color="white" _hover={{ bg:'#c05600' }} onClick={() => handleUpdateAsset(asset.id)}>Save</Button>
                            </HStack>
                          </VStack>
                        ) : (
                          <Flex justify="space-between" align="flex-start">
                            <Box flex={1} minW={0}>
                              <HStack spacing={2} mb={0.5}>
                                <Badge colorScheme="orange" fontSize="8px">{asset.asset_type}</Badge>
                                <Text fontSize="12px" fontWeight="700" color="gray.800">{asset.title || '(untitled)'}</Text>
                              </HStack>
                              {asset.description && <Text fontSize="11px" color="gray.500" mb={1}>{asset.description}</Text>}
                              {asset.url && (
                                <HStack spacing={1}>
                                  <Text fontSize="10px" color="blue.500" noOfLines={1} flex={1}>{asset.url}</Text>
                                  <IconButton icon={<FiExternalLink size={11} />} size="xs" variant="ghost" colorScheme="blue"
                                    onClick={() => window.open(asset.url, '_blank')} />
                                </HStack>
                              )}
                              {asset.file_name && (
                                <Text fontSize="10px" color="gray.400">📄 {asset.file_name}</Text>
                              )}
                            </Box>
                            <HStack spacing={1} flexShrink={0} ml={2}>
                              <IconButton icon={<FiEdit2 size={11} />} size="xs" variant="ghost" colorScheme="gray"
                                onClick={() => { setAssetForm({ title:asset.title, description:asset.description||'', asset_type:asset.asset_type, url:asset.url||'' }); setEditingAssetId(asset.id); setAddingAsset(false); }} />
                              <IconButton icon={<FiTrash2 size={11} />} size="xs" variant="ghost" colorScheme="red"
                                onClick={() => handleDeleteAsset(asset.id)} />
                            </HStack>
                          </Flex>
                        )}
                      </Box>
                    ))}
                  </VStack>
                </TabPanel>

                {/* Contacts */}
                <TabPanel px={0}>
                  <VStack spacing={3} align="stretch">
                    <Flex justify="space-between" align="center">
                      <Text fontSize="12px" color="gray.500">{contacts.length} contact{contacts.length!==1?'s':''} with unique links</Text>
                      <Button size="xs" leftIcon={<FiPlus size={11} />} colorScheme="purple"
                        onClick={() => {
                          setAddingContact(true);
                          // Show project/task contacts first, all contacts if none
                          if (projectContacts.length > 0) {
                            setContactResults(projectContacts);
                          } else {
                            searchContacts('');
                          }
                        }}>Add Contact</Button>
                    </Flex>

                    {addingContact && (
                      <Box p={3} bg="gray.50" borderRadius="md">
                        <Text fontSize="12px" fontWeight="600" mb={2}>Search contacts</Text>
                        <Input size="sm" placeholder="Search all contacts…" value={contactSearch}
                          onChange={e => { setContactSearch(e.target.value); searchContacts(e.target.value); }} />
                        {!contactSearch && projectContacts.length > 0 && contactResults.length > 0 && (
                          <Text fontSize="9px" color="purple.500" mt={1} fontWeight="600">
                            Showing project/task contacts — type to search all
                          </Text>
                        )}
                        {contactResults.length > 0 && (
                          <Box mt={2} border="1px" borderColor="gray.200" borderRadius="md"
                            maxH="220px" overflowY="auto"
                            sx={{ '&::-webkit-scrollbar': { w:'4px' }, '&::-webkit-scrollbar-thumb': { bg:'gray.300', borderRadius:'2px' } }}>
                            {contactResults.map(c => (
                              <Flex key={c.id} px={3} py={2} bg="white" align="center" justify="space-between"
                                cursor="pointer" _hover={{ bg:'purple.50' }} borderBottom="1px" borderColor="gray.50"
                                onClick={() => addContact(c)}>
                                <Box>
                                  <Text fontSize="12px" fontWeight="600">
                                    {[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}
                                  </Text>
                                  {c.email && <Text fontSize="10px" color="gray.400">{c.email}</Text>}
                                  {c.company_name && <Text fontSize="10px" color="gray.400">{c.company_name}</Text>}
                                </Box>
                                <FiPlus size={14} color="purple" />
                              </Flex>
                            ))}
                            {contactResults.length === 50 && (
                              <Box px={3} py={1.5} bg="gray.50" borderTop="1px" borderColor="gray.100">
                                <Text fontSize="10px" color="gray.400">Showing first 50 — type to filter</Text>
                              </Box>
                            )}
                          </Box>
                        )}
                        {contactSearch.length > 0 && contactResults.length === 0 && (
                          <Text fontSize="11px" color="gray.400" mt={2}>No contacts found</Text>
                        )}
                        <Button size="xs" variant="ghost" mt={2} onClick={() => { setAddingContact(false); setContactSearch(''); setContactResults([]); }}>Cancel</Button>
                      </Box>
                    )}

                    {contacts.length > 0 && (
                      <Box overflowX="auto">
                        <Table size="sm">
                          <Thead><Tr>
                            <Th px={2} fontSize="9px">Contact</Th>
                            <Th px={2} fontSize="9px">Email</Th>
                            <Th px={2} fontSize="9px">Link</Th>
                            <Th px={2} fontSize="9px">Views</Th>
                            <Th px={2} fontSize="9px">Approval</Th>
                            <Th px={2} fontSize="9px">Responses</Th>
                            <Th px={2} fontSize="9px">Last IP</Th>
                            <Th px={1} w="30px"></Th>
                          </Tr></Thead>
                          <Tbody>
                            {contacts.map(c => (
                              <ContactRow key={c.id} c={c} itemId={selected.id} onRemove={removeContact} />
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    )}
                    {contacts.length === 0 && !addingContact && (
                      <Text fontSize="12px" color="gray.400" textAlign="center" py={4}>
                        No contacts added yet. Add contacts to issue unique feedback links.
                      </Text>
                    )}
                  </VStack>
                </TabPanel>

                {/* Views */}
                <TabPanel px={0}>
                  <HStack justify="space-between" mb={3}>
                    <Text fontSize="12px" color="gray.500">{views.length} view session{views.length!==1?'s':''}</Text>
                    <IconButton icon={<FiRefreshCw size={12} />} size="xs" variant="ghost"
                      onClick={() => fetchViews(selected.uuid || selected.id)} aria-label="Refresh" />
                  </HStack>
                  {views.length === 0 && <Text fontSize="12px" color="gray.400" textAlign="center" py={6}>No views recorded yet</Text>}
                  <Box overflowX="auto">
                    {views.length > 0 && (
                      <Table size="sm">
                        <Thead><Tr>
                          <Th px={2} fontSize="9px">Contact</Th>
                          <Th px={2} fontSize="9px">Opened</Th>
                          <Th px={2} fontSize="9px">Closed</Th>
                          <Th px={2} fontSize="9px">Duration</Th>
                          <Th px={2} fontSize="9px">IP Address</Th>
                        </Tr></Thead>
                        <Tbody>
                          {views.map((v, i) => {
                            const name = v.label || [v.first_name, v.last_name].filter(Boolean).join(' ') || '—';
                            const opened = v.started_at ? new Date(v.started_at).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : '—';
                            const closed = v.ended_at  ? new Date(v.ended_at).toLocaleString('en-GB', { day:'2-digit', month:'short', hour:'2-digit', minute:'2-digit' }) : <Badge colorScheme="green" fontSize="7px">Open</Badge>;
                            const dur = v.duration_seconds > 0 ? `${Math.floor(v.duration_seconds/60)}m ${v.duration_seconds%60}s` : '—';
                            return (
                              <Tr key={i} _hover={{ bg:'gray.50' }}>
                                <Td px={2} fontSize="11px" fontWeight="600">{name}</Td>
                                <Td px={2} fontSize="10px" color="gray.600">{opened}</Td>
                                <Td px={2} fontSize="10px" color="gray.600">{closed}</Td>
                                <Td px={2} fontSize="10px" color="gray.600">{dur}</Td>
                                <Td px={2} fontSize="10px" color="gray.500" fontFamily="mono">{v.ip_address || '—'}</Td>
                              </Tr>
                            );
                          })}
                        </Tbody>
                      </Table>
                    )}
                  </Box>
                </TabPanel>

                {/* Responses */}
                <TabPanel px={0}>
                  <HStack justify="space-between" mb={3}>
                    <Text fontSize="12px" color="gray.500">{responses.length} response{responses.length!==1?'s':''}</Text>
                    <IconButton icon={<FiRefreshCw size={12} />} size="xs" variant="ghost"
                      onClick={() => fetchResponses(selected.uuid || selected.id)} />
                  </HStack>
                  {responses.length === 0 && <Text fontSize="12px" color="gray.400" textAlign="center" py={6}>No responses yet</Text>}
                  <VStack spacing={2} align="stretch">
                    {responses.map((r, i) => <ResponseRow key={i} r={r} />)}
                  </VStack>
                </TabPanel>

                {/* Edit */}
                {editing && (
                  <TabPanel px={0}>
                    <Tabs size="sm" colorScheme="purple">
                      <TabList><Tab>Content</Tab><Tab>Settings</Tab></TabList>
                      <TabPanels>
                        <TabPanel px={0}>{contentFormJSX}</TabPanel>
                        <TabPanel px={0}>{settingsFormJSX}</TabPanel>
                      </TabPanels>
                    </Tabs>
                    <HStack mt={4} justify="flex-end">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
                      <Button size="sm" colorScheme="purple" onClick={handleUpdate}>Save Changes</Button>
                    </HStack>
                  </TabPanel>
                )}
              </TabPanels>
            </Tabs>
          </ModalBody>
          <ModalFooter><Button size="sm" onClick={() => { setSelected(null); }}>← Back to list</Button></ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size={creating ? '2xl' : 'xl'} scrollBehavior="inside">
      <ModalOverlay /><ModalContent maxH="80vh" mb="60px">
        <ModalHeader fontSize="md" pb={2}>
          <HStack spacing={2}>
            <FiMessageSquare />
            <Text>Client Feedback</Text>
            {taskTitle && <Text fontSize="11px" color="gray.400" fontWeight="400">— {taskTitle}</Text>}
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto">
          {creating ? (
            <VStack spacing={4} align="stretch">
              <Tabs size="sm" colorScheme="purple">
                <TabList><Tab>Content</Tab><Tab>Settings</Tab></TabList>
                <TabPanels>
                  <TabPanel px={0}>{contentFormJSX}</TabPanel>
                  <TabPanel px={0}>{settingsFormJSX}</TabPanel>
                </TabPanels>
              </Tabs>
              {form.sub_task_id && (
                <Box px={3} py={2} bg="purple.50" borderRadius="md" border="1px" borderColor="purple.200">
                  <Text fontSize="11px" color="purple.600">
                    🔹 Creating for sub-task: <strong>{allSubTasks.find(s=>s.id===form.sub_task_id)?.title || allSubTasks.find(s=>s.id===form.sub_task_id)?.description || `Sub-task ${form.sub_task_id}`}</strong>
                  </Text>
                </Box>
              )}
              <HStack justify="flex-end">
                <Button size="sm" variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
                <Button size="sm" bg="#EE7203" color="white" _hover={{ bg: "#c05600" }} leftIcon={<FiCheck size={13} />} onClick={handleCreate}>Create Feedback Item</Button>
              </HStack>
            </VStack>
          ) : (
            <VStack spacing={2} align="stretch">
              <Flex justify="space-between" align="center" mb={2}>
                <VStack align="flex-start" spacing={0}>
                  <Text fontSize="13px" fontWeight="700" color="gray.700">Client Feedback</Text>
                  <Text fontSize="11px" color="gray.400">{items.filter(i=>!i.is_archived).length} active across {allSubTasks.length + 1} level{allSubTasks.length > 0 ? 's' : ''}</Text>
                </VStack>
                <Button size="sm" leftIcon={<FiPlus size={13} />} bg="#EE7203" color="white" _hover={{ bg:'#c05600' }}
                  onClick={() => { setForm({ title:taskTitle||'', description:'', status_update:'', version_number:'1.0', content_type:'youtube', content_url:'', file_id:null, allow_resharing:false, password:'', feedback_types:['text'], contacts_can_see_all_feedback:false, sub_task_id:'' }); setVersionSourceId(null); setCreating(true); }}>
                  New Item
                </Button>
              </Flex>
              {loading && <Spinner size="sm" color="purple.500" mx="auto" my={4} />}
              {!loading && items.length === 0 && (
                <Box textAlign="center" py={8}>
                  <Box fontSize="36px" mb={3}>💬</Box>
                  <Text fontSize="13px" color="gray.500">No feedback items yet</Text>
                  <Text fontSize="11px" color="gray.400" mt={1}>Share videos, documents or links with clients and collect structured feedback</Text>
                </Box>
              )}
              {/* Group items: task-level first, then per sub-task */}
              {(() => {
                const taskItems = items.filter(i => !i.sub_task_id);
                const subTaskGroups = allSubTasks.map(st => ({
                  subTask: st,
                  items: items.filter(i => i.sub_task_id === st.id),
                }));
                // Catch any items whose sub_task_id doesn't match a loaded sub-task
                const knownSubTaskIds = new Set(allSubTasks.map(s => s.id));
                const orphanedItems = items.filter(i => i.sub_task_id && !knownSubTaskIds.has(i.sub_task_id));
                const openEditItem = (e, item) => {
                  e.stopPropagation();
                  let ft;
                  try {
                    ft = Array.isArray(item.feedback_types)
                      ? item.feedback_types
                      : JSON.parse(item.feedback_types || '["text"]');
                  } catch { ft = ['text']; }
                  setForm({
                    title: item.title,
                    description: item.description || '',
                    status_update: item.status_update || '',
                    version_number: item.version_number || '1.0',
                    content_type: item.content_type,
                    content_url: item.content_url || '',
                    file_id: item.file_id || null,
                    allow_resharing: !!item.allow_resharing,
                    password: '',
                    feedback_types: ft,
                    contacts_can_see_all_feedback: !!item.contacts_can_see_all_feedback,
                    sub_task_id: item.sub_task_id || '',
                  });
                  setAttachedFileName(item.file_name || '');
                  setEditingItem(item); // open standalone edit modal, don't navigate to detail
                };
                const ItemCard = ({ item }) => (
                  <Flex key={item.id} p={3} border="1px" borderRadius="md" align="center" justify="space-between"
                    bg={item.is_archived ? 'gray.50' : 'white'} opacity={item.is_archived ? 0.7 : 1}
                    cursor="pointer" _hover={{ borderColor:'purple.300', bg:'purple.50' }}
                    onClick={() => setSelected(item)}>
                    <Box minW={0} flex={1}>
                      <HStack spacing={2} flexWrap="wrap">
                        <Text fontSize="13px" fontWeight="700" color={item.is_archived ? 'gray.400' : 'gray.800'} noOfLines={1}>{item.title}</Text>
                        <Badge colorScheme="purple" fontSize="8px">v{item.version_number}</Badge>
                        {!!item.is_archived && <Badge colorScheme="gray" fontSize="8px">Archived</Badge>}
                        <Badge colorScheme="blue" fontSize="7px">{item.content_type}</Badge>
                        {(item.assets?.length > 0) && <Badge colorScheme="orange" fontSize="7px">+{item.assets.length} assets</Badge>}
                      </HStack>
                      <Text fontSize="10px" color="gray.400" mt={0.5}>{item.contact_count} contacts · {item.response_count} responses · {item.viewed_count} viewed{item.approval_count > 0 ? ` · ✅ Approved by ${item.approval_count}` : ''}</Text>
                    </Box>
                    <HStack spacing={1} flexShrink={0} ml={2}>
                      <Tooltip label="Edit" fontSize="xs">
                        <IconButton icon={<FiEdit2 size={12} />} size="xs" variant="ghost" colorScheme="gray"
                          aria-label="Edit feedback item"
                          onClick={e => openEditItem(e, item)} />
                      </Tooltip>
                      <Badge colorScheme="purple" variant="outline" fontSize="9px">{item.contact_count} links →</Badge>
                    </HStack>
                  </Flex>
                );
                return (
                  <VStack spacing={3} align="stretch">
                    {/* Task-level items */}
                    {taskItems.length > 0 && (
                      <Box>
                        <HStack mb={1} justify="space-between">
                          <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wide">
                            📋 Task Level
                          </Text>
                          <Button size="xs" variant="ghost" colorScheme="purple" leftIcon={<FiPlus size={10} />}
                            onClick={() => { setForm(p => ({...p, sub_task_id:'', title: taskTitle||'' })); setVersionSourceId(null); setCreating(true); }}>
                            Add
                          </Button>
                        </HStack>
                        <VStack spacing={1} align="stretch">
                          {taskItems.map(item => <ItemCard key={item.id} item={item} />)}
                        </VStack>
                      </Box>
                    )}
                    {taskItems.length === 0 && (
                      <Flex p={3} border="1px" borderStyle="dashed" borderColor="purple.200" borderRadius="md"
                        align="center" justify="space-between" bg="purple.50" cursor="pointer"
                        onClick={() => { setForm(p => ({...p, sub_task_id:'', title: taskTitle||'' })); setVersionSourceId(null); setCreating(true); }}>
                        <Text fontSize="12px" color="purple.400">+ Add task-level feedback</Text>
                      </Flex>
                    )}
                    {/* Sub-task groups — only show if they have items */}
                    {subTaskGroups.filter(g => g.items.length > 0).length > 0 && (
                      <Box>
                        <Text fontSize="10px" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={2}>
                          🔹 Sub-Tasks
                        </Text>
                        <VStack spacing={3} align="stretch">
                          {subTaskGroups.filter(g => g.items.length > 0).map(({ subTask, items: stItems }) => (
                            <Box key={subTask.id} border="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
                              <Flex bg="gray.50" px={3} py={2} align="center" justify="space-between"
                                borderBottom="1px" borderColor="gray.200">
                                <HStack spacing={2}>
                                  <Box w="6px" h="6px" borderRadius="full" bg="purple.400" />
                                  <Text fontSize="12px" fontWeight="700" color="gray.700" noOfLines={1}>
                                    {subTask.title || subTask.description || `Sub-task ${subTask.id}`}
                                  </Text>
                                  <Badge colorScheme="purple" fontSize="7px" variant="outline">{stItems.length}</Badge>
                                </HStack>
                                <Button size="xs" variant="ghost" colorScheme="purple" leftIcon={<FiPlus size={10} />}
                                  onClick={() => { setForm(p => ({...p, sub_task_id: subTask.id, title: subTask.title || subTask.description || '' })); setVersionSourceId(null); setCreating(true); }}>
                                  Add
                                </Button>
                              </Flex>
                              <VStack spacing={0} align="stretch" divider={<Box h="1px" bg="gray.100" />}>
                                {stItems.map(item => (
                                  <Box px={1} py={1} key={item.id}>
                                    <ItemCard item={item} />
                                  </Box>
                                ))}
                              </VStack>
                            </Box>
                          ))}
                        </VStack>
                      </Box>
                    )}
                    {/* Orphaned items — only show once sub-tasks have loaded to avoid false positives */}
                    {subTasksLoaded && orphanedItems.length > 0 && (
                      <Box>
                        <Text fontSize="10px" fontWeight="700" color="gray.400" textTransform="uppercase" letterSpacing="wide" mb={1}>
                          ⚠ Sub-task not found
                        </Text>
                        <VStack spacing={1} align="stretch">
                          {orphanedItems.map(item => <ItemCard key={item.id} item={item} />)}
                        </VStack>
                      </Box>
                    )}
                  </VStack>
                );
              })()}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter><Button size="sm" onClick={onClose}>Close</Button></ModalFooter>
      </ModalContent>
    </Modal>

    {/* Standalone edit modal — opened from pencil icon on list, no navigation */}
    {editingItem && (
      <Modal isOpen={true} onClose={() => setEditingItem(null)} size="2xl" scrollBehavior="inside" motionPreset="none">
        <ModalOverlay />
        <ModalContent maxH="75vh" mt="5vh" mb="5vh" overflowY="auto">
          <ModalHeader fontSize="md" pb={1}>
            <HStack spacing={2}>
              <FiEdit2 size={15} />
              <Text>Edit Feedback Item</Text>
              <Badge colorScheme="purple" fontSize="9px">v{editingItem.version_number}</Badge>
              {(editingItem.password_hash || editingItem.password_protected) ? <Badge colorScheme="orange" fontSize="8px">🔒 Password protected</Badge> : null}
            </HStack>
          </ModalHeader>
          <ModalCloseButton onClick={() => setEditingItem(null)} />
          <ModalBody>
            <Tabs size="sm" colorScheme="purple">
              <TabList><Tab>Content</Tab><Tab>Settings</Tab></TabList>
              <TabPanels>
                <TabPanel px={0}>{contentFormJSX}</TabPanel>
                <TabPanel px={0}>{settingsFormJSX}</TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={() => setEditingItem(null)}>Cancel</Button>
            <Button size="sm" colorScheme="purple" onClick={async () => {
              try {
                // '__CLEAR__' sentinel means explicitly clear the password
                const payload = { ...form };
                if (payload.password === '__CLEAR__') payload.password = '';
                const { data } = await api.put(`/feedback/${editingItem.uuid || editingItem.id}`, payload);
                setItems(prev => prev.map(i => i.id === data.id ? data : i));
                setEditingItem(null);
                toast({ title: 'Updated', status: 'success', duration: 1500 });
              } catch(e) { toast({ title: 'Update failed', status: 'error', duration: 2000 }); }
            }}>Save Changes</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    )}
    </>
  );
}
